import type { MySurveyTrainingRecommendation, TrainingDecisionValue } from '../surveys/mySurveyTypes';

/**
 * One training recommendation SENT to the signed-in employee, with where they stand on it —
 * `GET /api/self-service/training-recommendations`. Nobody else's anything: who else was sent it,
 * and what they answered, is Learning & Development's question — and so is who wrote, approved or
 * sent it, so the row carries no provenance.
 */
export interface MyTrainingRecommendation {
  /** The RECOMMENDATION's id — what the row opens by. */
  id: string;
  title: string;
  /** The survey it was recommended from — where every act on it is made. */
  surveyId: string;
  surveyTitle: string;
  /** When it was sent to the employee — an instant. */
  sentAt: string;
  /** Their OWN answer to it — `TRAINING_DECISION`, numeric on the wire. */
  decision: TrainingDecisionValue;
  decidedAt?: string | null;
  /** Calendar dates, `YYYY-MM-DD` — never instants; format with `formatDateOnly`. Absent until they start one. */
  trainingStartDate?: string | null;
  trainingEndDate?: string | null;
  trainingCompletedAt?: string | null;
  /** How it ended — `TRAINING_RESULT` (0 Completed, 1 Cancelled). Absent while it has not. */
  trainingResult?: 0 | 1 | null;
  reportAt?: string | null;
  /**
   * Whether the SURVEY it came from is live. A deleted recommendation never arrives at all; on a
   * deleted survey the track is read, never acted on.
   */
  isActive: boolean;
}

/** One recommendation with the reader's whole track on it — the profile's read. */
export interface MyTrainingRecommendationDetail {
  /** What was recommended and the track — the shape Self Service › Survey reads too, so its forms take it as it is. */
  recommendation: MySurveyTrainingRecommendation;
  surveyId: string;
  surveyTitle: string;
  /** Whether the SURVEY it came from is live. */
  isActive: boolean;
}

/** Whether the account can be sent a recommendation at all. `error` is absent when it can. */
export interface MyTrainingRecommendationAccount {
  error?: string | null;
}

/**
 * The employee's own words for their answer — the server's (`MySurveyService.SentDecisionLabel`),
 * so the row, the filter and the pill read one vocabulary.
 */
export const MY_TRAINING_DECISION_LABELS: Record<TrainingDecisionValue, string> = {
  0: 'Pending',
  1: 'Accepted',
  2: 'Declined',
};
